<template>
  <div
    class="relative flex max-h-full min-h-[300px] w-full max-w-[1140px] flex-col overflow-auto rounded-3xl bg-white p-4 text-black lg:overflow-hidden lg:p-6">
    <FormSuccess v-if="formState === FormState.SUCCESS" />
    <FormError v-else-if="formState === FormState.ERROR" />
    <form
      v-else
      @submit="submitForm">
      <p class="text-right text-xs text-gray-300">
        {{ $t('form.fields.mandatory') }}
      </p>
      <h3 class="my-4 text-2xl">
        {{ $t('form.yourInformation') }}
      </h3>
      <div class="mb-6 grid grid-cols-1 items-end gap-6 text-left md:grid-cols-2">
        <FormInput
          v-model="inputs.parentFirstName"
          name="firstName"
          type="text"
          :label="$t('form.fields.firstName.label')"
          :placeholder="$t('form.fields.firstName.placeholder')"
          :state="formState"
          required />
        <FormInput
          v-model="inputs.parentLastName"
          name="lastName"
          type="text"
          :label="$t('form.fields.lastName.label')"
          :placeholder="$t('form.fields.lastName.placeholder')"
          :state="formState"
          required />
        <FormInput
          v-model="inputs.email"
          name="email"
          type="email"
          :label="$t('form.fields.email.label')"
          :placeholder="$t('form.fields.email.placeholder')"
          :state="formState"
          required />
        <FormInput
          v-model="inputs.phone"
          name="phone"
          type="tel"
          pattern="[0-9\s\+\-]*"
          :title="$t('form.fields.phone.title')"
          :label="$t('form.fields.phone.label')"
          :placeholder="$t('form.fields.phone.placeholder')"
          :state="formState"
          required />
      </div>
      <h3 class="my-4 text-2xl">
        {{ $t('form.childInformation') }}
      </h3>
      <div class="mb-6 grid grid-cols-1 items-end gap-6 text-left md:grid-cols-2">
        <FormInput
          v-model="inputs.childFirstName"
          name="childFirstName"
          type="text"
          :label="$t('form.fields.firstName.label')"
          :placeholder="$t('form.fields.firstName.placeholder')"
          :state="formState"
          required />
        <FormInput
          v-model="inputs.childLastName"
          name="childLastName"
          type="text"
          :label="$t('form.fields.lastName.label')"
          :placeholder="$t('form.fields.lastName.placeholder')"
          :state="formState"
          required />
        <FormInput
          v-model="inputs.childDateOfBirth"
          name="childDateOfBirth"
          type="date"
          :label="$t('form.fields.dateOfBirth.label')"
          :placeholder="$t('form.fields.dateOfBirth.placeholder')"
          :max="getDateStringYearsInPast(0)"
          :state="formState"
          required />
        <FormDropdown
          v-model="inputs.childGenderId"
          name="childGender"
          :label="$t('form.fields.gender.label')"
          :placeholder="$t('form.fields.gender.placeholder')"
          :state="formState"
          :options="[
            { label: $t('form.fields.gender.options.male'), value: 'male' },
            { label: $t('form.fields.gender.options.female'), value: 'female' }
          ]"
          required />
        <FormDropdown
          v-model="inputs.childExperienceId"
          name="childExperience"
          :label="$t('form.fields.experience.childLabel')"
          :placeholder="$t('form.fields.experience.placeholder')"
          :state="formState"
          :options="[
            { label: $t('form.fields.experience.options.yes'), value: 'yes' },
            { label: $t('form.fields.experience.options.no'), value: 'no' },
            { label: $t('form.fields.experience.options.unknown'), value: 'unknown' }
          ]"
          required />
        <FormInput
          v-model="inputs.childExperienceDescription"
          name="childExperienceDescription"
          type="text"
          :label="$t('form.fields.experienceDescription.childLabel')"
          :placeholder="$t('form.fields.experienceDescription.placeholder')"
          :state="formState" />
      </div>
      <FormCheckbox
        v-model="inputs.privacy"
        name="privacy"
        :copy="$t('form.fields.privacy.copy')"
        :link="{
          label: $t('form.fields.privacy.link.label'),
          href: $t('form.fields.privacy.link.href')
        }"
        :state="formState"
        required />
      <FormButton
        type="submit"
        :label="$t('form.fields.submit')"
        class="ml-auto mt-4 w-auto"
        :state="formState" />
    </form>
  </div>
</template>

<script setup lang="ts">
  import { reactive, ref, useAppConfig } from '#imports'

  import { FormState, FormVariant } from '~/constants/form'
  import { EventName, PropName } from '~/constants/plausible'
  import { getDateStringYearsInPast } from '~/utils/helpers'

  type Inputs = {
    parentFirstName: string
    parentLastName: string
    email: string
    phone: string
    childFirstName: string
    childLastName: string
    childDateOfBirth: Date
    childGenderId: string
    childExperienceId: string
    childExperienceDescription?: string
    privacy: boolean
  }

  const appConfig = useAppConfig()
  const visual7BeHostname = appConfig.visual7BeHostname
  const formState = ref(FormState.IDLE)
  const { trackEvent } = usePlausible()

  const inputs = reactive<Inputs>({
    parentFirstName: '',
    parentLastName: '',
    email: '',
    phone: '',
    childFirstName: '',
    childLastName: '',
    childDateOfBirth: null,
    childGenderId: '',
    childExperienceId: '',
    childExperienceDescription: '',
    privacy: false
  })

  async function submitForm(event: Event) {
    event.preventDefault()

    formState.value = FormState.LOADING

    const formData = {
      isChild: true,
      parentName: `${inputs.parentFirstName} ${inputs.parentLastName}`,
      name: `${inputs.childFirstName} ${inputs.childLastName}`,
      dateOfBirth: inputs.childDateOfBirth,
      genderId: inputs.childGenderId,
      email: inputs.email,
      phone: inputs.phone,
      experienceId: inputs.childExperienceId,
      experienceDescription: inputs.childExperienceDescription,
      consentDate: new Date()
    }

    await fetch(`${visual7BeHostname}/testerClub/applications`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    })
      .then(async response => {
        if (response.ok) {
          // @ts-expect-error: Meta pixel does not provide types
          if (window.fbq) {
            fbq('track', 'SubmitApplication', {
              content_name: 'O7 Tester Club child application'
            })
          }

          trackEvent(EventName.SUBMIT_APPLICATION, {
            props: { [PropName.FORM_VARIANT]: FormVariant.CHILD }
          })

          setTimeout(() => {
            formState.value = FormState.SUCCESS

            inputs.parentFirstName = ''
            inputs.parentLastName = ''
            inputs.email = ''
            inputs.phone = ''
            inputs.childFirstName = ''
            inputs.childLastName = ''
            inputs.childDateOfBirth = null
            inputs.childGenderId = ''
            inputs.childExperienceId = ''
            inputs.childExperienceDescription = ''
            inputs.privacy = false
          }, 500)
        } else {
          formState.value = FormState.ERROR
          throw new Error(await response.json())
        }
      })
      .catch(error => {
        formState.value = FormState.ERROR
        console.error(error)
      })
  }
</script>
